/**
 * Arborescence des dossiers (04/11/2026, demande du dirigeant : « faire une arborescence avec les dossiers, que ce soit
 * intuitif ») : Dossier › Pièces du dossier / Apprenant › ses pièces, avec l'état de chaque élément. À gauche l'arbre
 * (clavier : flèches, Entrée, lettres), à droite le détail du nœud choisi et le lien vers la fiche du dossier.
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FolderTree, Search } from "lucide-react";
import { arbreFormateur, LIBELLES_ETAT, type NoeudArbre } from "@/domaine/accueil-apprenant/arbre";
import type { arborescenceDossiers } from "@/serveur/services/arborescence";
import { api, type Json } from "../api";
import { Arborescence, PastilleEtat } from "../ui/Arborescence";
import {
  Alerte,
  Carte,
  Chargement,
  classesLienBouton,
  EtatVide,
  ILLUSTRATIONS,
  TitrePage,
  useTitreDocument,
} from "../ui/base";

type DossierArbreApi = Json<Awaited<ReturnType<typeof arborescenceDossiers>>>[number];

function trouver(racines: readonly NoeudArbre[], id: string | null): NoeudArbre | null {
  if (!id) return null;
  for (const n of racines) {
    if (n.id === id) return n;
    const t = trouver(n.enfants, id);
    if (t) return t;
  }
  return null;
}

export function ArborescenceDossiers() {
  useTitreDocument("Arborescence des dossiers");
  const q = useQuery({
    queryKey: ["arborescence"],
    queryFn: () => api.get<DossierArbreApi[]>("/arborescence"),
  });
  const [recherche, setRecherche] = useState("");
  const [archives, setArchives] = useState(false);
  const [selection, setSelection] = useState<string | null>(null);

  const racines = useMemo(() => {
    const t = recherche.trim().toLocaleLowerCase("fr-FR");
    const dossiers = (q.data ?? []).filter(
      (d) =>
        (archives || !d.archive) &&
        (!t ||
          [d.reference, d.titre, ...d.stagiaires.map((s) => s.nom)]
            .join(" ")
            .toLocaleLowerCase("fr-FR")
            .includes(t)),
    );
    return arbreFormateur(dossiers);
  }, [q.data, recherche, archives]);
  const choisi = trouver(racines, selection) ?? racines[0] ?? null;

  if (q.isPending) return <Chargement />;
  if (q.error) return <Alerte ton="danger">{q.error.message}</Alerte>;

  return (
    <div className="mx-auto max-w-6xl">
      <TitrePage
        titre="Arborescence des dossiers"
        soustitre="Parcourez vos dossiers comme des dossiers de fichiers : chaque apprenant, chaque pièce, et ce qui reste à faire."
      />
      {q.data.length === 0 ? (
        <EtatVide image={ILLUSTRATIONS.bureau} titre="Aucun dossier pour l'instant">
          Vos dossiers apparaîtront ici dès leur création dans le générateur de conventions.
        </EtatVide>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <label className="relative min-w-0 flex-1 sm:max-w-sm">
              <span className="sr-only">Filtrer les dossiers</span>
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-encre-3"
                aria-hidden
              />
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Référence, formation, apprenant…"
                className="h-10 w-full rounded-sm border border-trait-fort bg-carte pr-3 pl-9 text-sm hover:border-encre-3 focus:border-accent focus:ring-[3px] focus:ring-accent/40 focus:outline-none"
              />
            </label>
            <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm text-encre-2">
              <input
                type="checkbox"
                checked={archives}
                onChange={(e) => setArchives(e.target.checked)}
                className="size-4 accent-(--color-accent)"
              />
              Afficher les dossiers archivés
            </label>
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
            <Carte className="p-2" data-testid="arbre-dossiers">
              {racines.length === 0 ? (
                <p className="p-4 text-sm text-encre-2">Aucun dossier ne correspond.</p>
              ) : (
                <Arborescence
                  racines={racines}
                  libelle="Mes dossiers"
                  ouvertsInitiaux={racines.slice(0, 1).map((r) => r.id)}
                  selection={choisi?.id ?? null}
                  activer={(n) => setSelection(n.id)}
                />
              )}
            </Carte>
            {choisi && <Detail noeud={choisi} />}
          </div>
        </>
      )}
    </div>
  );
}

function Detail({ noeud }: { noeud: NoeudArbre }) {
  return (
    <Carte className="self-start p-5 lg:sticky lg:top-4" aria-live="polite" data-testid="detail-noeud">
      <p className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-encre-3 uppercase">
        <FolderTree className="size-4 text-accent" aria-hidden />
        {
          {
            dossier: "Dossier",
            apprenant: "Apprenant",
            groupe: "Pièces communes",
            document: "Pièce",
            formulaire: "Formulaire",
            etape: "Étape",
            formation: "Formation",
            supports: "Supports",
          }[noeud.genre]
        }
      </p>
      <h2 className="mt-1 text-lg leading-snug font-semibold">{noeud.libelle}</h2>
      {noeud.etat && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-encre-2">
          {noeud.genre === "dossier" && <span>Pièces :</span>}
          <PastilleEtat etat={noeud.etat} detail={LIBELLES_ETAT[noeud.etat]} />
          {noeud.detail && noeud.detail !== LIBELLES_ETAT[noeud.etat] && (
            <span>{noeud.genre === "dossier" ? `· Statut du dossier : ${noeud.detail}` : noeud.detail}</span>
          )}
        </p>
      )}
      {noeud.enfants.length > 0 && (
        <ul className="mt-4 divide-y divide-trait rounded-md border border-trait">
          {noeud.enfants.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
              <span className="min-w-0 truncate">{e.libelle}</span>
              {e.etat && <PastilleEtat etat={e.etat} detail={e.detail} />}
            </li>
          ))}
        </ul>
      )}
      {noeud.cible?.startsWith("/dossiers/") && (
        <Link
          to="/dossiers/$id"
          params={{ id: noeud.cible.split("/")[2]! }}
          className={`${classesLienBouton("primaire", "sm")} mt-4`}
        >
          Ouvrir le dossier <ArrowRight className="size-4" aria-hidden />
        </Link>
      )}
    </Carte>
  );
}
